import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import classNames from 'classnames';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ReactTable, Spinner, SkeletonLine, Checkbox, Button, DateRangePicker, Tooltip } from '@crazyegginc/hatch';
import { Modal } from '/src/contexts/modal';
import { fromUnixTime, getUnixTime, startOfDay, endOfDay } from 'date-fns';
import { useAtom, atom } from 'jotai';
import { abTestTypes, abTestVariantTypes } from '/src/features/ab-testing/constants';

import { LazyImg } from '/src/components/LazyImg';
import { SelectionProvider } from '/src/contexts/selection';
import { clampDateRange, formatLongDate, getTimeZoneOffset } from '/src/utils/date';
import { displayUrl } from '/src/utils/url';
import { CreateAbTestButton } from '../components/CreateAbTestButton';
import { SEO } from '/src/components/SEO';
import { DashHeader } from '/src/components/headers/DashHeader';
import { DashboardPage } from '/src/components/Page';
import { LinkWithParams } from '/src/components/LinkWithParams';
import { Breadcrumb, BreadcrumbItem } from '/src/components/Breadcrumb';

import { SORT_ORDER_TYPES, TIMEZONES } from '/src/features/_global/constants';
import { camelToSnake, snakeToCamel } from '/src/utils/string';

import { useModal, useAuthContext, useNotifications, usePermissions, useSelection, useQueryParams } from '/src/hooks';
import { SPECIAL_DATE_RANGES } from '/src/features/_global/constants';

import { AbTestDetailModal } from '../components/modals/AbTestDetailModal';
import { NotesModal } from '../components/modals/NotesModal';
import { AbTestActionButton } from '../components/AbTestActionButton';
import { SummaryRow, SummaryCard, SummaryGoalSelector } from '../components/SummaryRow';
import {
  DistributionChart,
  abTestDistributionDatasetAtom,
} from '/src/features/ab-testing/components/DistributionChart';
import { getConvertedDateRange } from '/src/utils/date';

import { AddSplitVariant } from '../components/modals/AddSplitVariant';
import { EditSplitVariant } from '../components/modals/EditSplitVariant';
// import { AbTestRenameModal } from '../components/modals/AbTestRenameModal';

import { formatCurrency, isProduction, isSharedResourceSubscriptionError } from '/src/utils';
import { RateDelta } from '../utils';

import { abTestDetailV2Query } from '../queries';
import { goalRefListQuery } from '/src/features/goals/queries';
import { FEATURES } from '/src/features/_global/constants';
import { AB_TEST_STATUSES } from '../constants';

import { ReactComponent as DescriptionIcon } from '@crazyegginc/hatch/dist/images/icon-page-w-text-outline.svg';
import { ReactComponent as NotesIcon } from '@crazyegginc/hatch/dist/images/icon-form.svg';
import { ReactComponent as NotesAddedIcon } from '@crazyegginc/hatch/dist/images/icon-form-tick.svg';
import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow.svg';
import { ReactComponent as InfoIcon } from '@crazyegginc/hatch/dist/images/icon-info-circle-outline.svg';
import { VariantActionButton } from '../components/VariantActionButton';
import { ReactComponent as EyeIcon } from '@crazyegginc/hatch/dist/images/icon-watched-outine.svg';
import { ReactComponent as ImgPlaceholderIcon } from '@crazyegginc/hatch/dist/images/illustration-thumbnail-placeholder.svg';
import { AbTestNotFoundWall } from '../components/paywalls/NotFoundWall';
import { atomWithDefault, useResetAtom } from 'jotai/utils';
import { AppLink } from '/src/features/_global/components/AppLink';

const isProd = isProduction();

export const abTestSelectedDatesAtom = atomWithDefault(() => {
  const params = new URLSearchParams(window.location.search);
  const dateRange = params.get('date_range');

  if (dateRange && dateRange.includes(',')) {
    const [startDate, endDate] = dateRange.split(',').map(Number);
    return {
      special: null,
      startDate,
      endDate,
    };
  } else if (dateRange && dateRange.length) {
    return {
      special: dateRange,
      startDate: null,
      endDate: null,
    };
  } else {
    return {
      special: 'default',
      startDate: null,
      endDate: null,
    };
  }
});

const selectedGoalIdAtom = atomWithDefault(() => {
  const params = new URLSearchParams(window.location.search);
  return Number(params.get('goal'));
});

function VariantScreenshotModal({ variant }) {
  const modal = useModal();
  const notifications = useNotifications();

  return (
    <Modal dialogClassName="!w-auto max-w-[90vw] max-h-[90vh] pt-10">
      <img
        src={variant.screenshotUrl}
        alt="screenshot"
        onError={() => {
          notifications.info({
            title: 'Screenshot still processing',
            content: `This variant's screenshot is still being processed. Please check back later.`,
            timeout: 3000,
          });
          modal.close();
        }}
      />
    </Modal>
  );
}

// function variantResultSortFunc(variant1, variant2) {
//   return (variant2.results.improvementFromControl || 0) - (variant1.results.improvementFromControl || 0);
// }
//
// function variantPositionSortFunc(variant1, variant2) {
//   const firstVariant = variant1.position || 0;
//   const secondVariant = variant2.position || 0;
//
//   if (firstVariant > secondVariant) return 1;
//   if (firstVariant < secondVariant) return -1;
//   return 0;
// }

function getSpecialDateRangeForTest(data) {
  if (data.stoppedAt || data.status === 'STOPPED') {
    const { startDate, endDate } = getConvertedDateRange(
      { special: SPECIAL_DATE_RANGES.LAST_100_DAYS },
      true,
      fromUnixTime(data.stoppedAt),
    );
    return clampDateRange({ startDate, endDate }, { clampStart: data.publishedAt, clampEnd: data.stoppedAt });
  }
  const { startDate, endDate } = getConvertedDateRange({ special: SPECIAL_DATE_RANGES.LAST_30_DAYS }, true);
  return clampDateRange({ startDate, endDate }, { clampStart: data.publishedAt, clampEnd: null });
}

const enableRetiredVariantsAtom = atom(false);

const timezoneOffset = getTimeZoneOffset();

function AbTestResults() {
  const { currentUser, isSharing } = useAuthContext();
  const defaultCurrency = currentUser?.settings?.defaultCurrency ?? 'USD';
  const { setSelection } = useSelection();
  const [selectedGoal, setSelectedGoal] = useState(null);
  const modal = useModal();
  const perfMetricsStarted = useRef(false);
  const variantsTableRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const notifications = useNotifications();
  const [selection, setDateSelection] = useAtom(abTestSelectedDatesAtom);
  const [dataset, setDataset] = useAtom(abTestDistributionDatasetAtom);
  const [selectedGoalId, setSelectedGoalId] = useAtom(selectedGoalIdAtom);
  const [searchParams] = useSearchParams();
  const order = searchParams.get('order') || 'NAME';
  const sort = searchParams.get('sort') || 'ASC';
  const [showRetiredVariants] = useAtom(enableRetiredVariantsAtom);
  const resetSelectedDates = useResetAtom(abTestSelectedDatesAtom);
  const resetDataset = useResetAtom(abTestDistributionDatasetAtom);
  const resetSelectedGoal = useResetAtom(selectedGoalIdAtom);
  const { set: paramSet } = useQueryParams();
  const [rawVariants, setRawVariants] = useState([]);

  const permissions = usePermissions();

  const setGoal = useCallback(
    (goal) => {
      if (!goal) return;
      setSelectedGoal(goal);
      setSelectedGoalId(goal.id);
      paramSet('goal', goal.id);
    },
    [setSelectedGoalId, setSelectedGoal, paramSet],
  );

  useEffect(() => {
    // reset in case there were any previously selected values.
    // it will still respect URL query params.
    resetSelectedDates();
    resetDataset();
    resetSelectedGoal();
  }, [resetSelectedDates, resetDataset, resetSelectedGoal]);

  const {
    data: resultData,
    isFetching,
    isInitialLoading,
    error,
    refetch,
  } = useQuery({
    ...abTestDetailV2Query({
      id: Number(id),
      goalId: selectedGoalId ? selectedGoalId : selectedGoal ? selectedGoal.id : undefined,
      presetDate: selection.special,
      startDate: selection.special ? undefined : selection.startDate + timezoneOffset,
      endDate: selection.special ? undefined : selection.endDate + timezoneOffset,
      includeGoals: true,
      order,
      sort,
    }),
    enabled: !!id,
    onSuccess(data) {
      if (!selectedGoal?.id) {
        setGoal({
          id: data?.abTestDetailV2?.selectedGoal?.id ?? data?.abTestDetailV2?.goal?.id,
          name: data?.abTestDetailV2?.selectedGoal?.name ?? data?.abTestDetailV2?.goal?.name,
        });
      }

      setRawVariants(data?.abTestDetailV2?.variants ?? []);
    },
    keepPreviousData: true,
    cacheTime: 0,
    networkMode: 'always',
  });

  const filteredVariants = useMemo(() => {
    return rawVariants.filter((v) => showRetiredVariants || !v.isRetired);
  }, [rawVariants, showRetiredVariants]);

  useEffect(() => {
    setSelection(filteredVariants);
  }, [filteredVariants, setSelection]);

  const { data: goalsData, isFetching: isFetchingGoals } = useQuery({
    ...goalRefListQuery({
      siteId: isSharing ? null : Number(resultData?.abTestDetailV2?.siteId),
    }),
    enabled: isSharing || !!resultData?.abTestDetailV2?.siteId,
  });

  useEffect(() => {
    if (window.CE2?.timing && isProd && !perfMetricsStarted.current) {
      window.CE2.timing.start('ab_test_detail_v2');
      perfMetricsStarted.current = true;
    }
  }, []);

  useEffect(() => {
    if (window.CE2?.timing && isProd && perfMetricsStarted.current && (resultData || error) && !isFetching) {
      window.CE2.timing.stop('ab_test_detail_v2');
      perfMetricsStarted.current = false;
    }
  }, [error, isFetching, resultData]);

  useEffect(() => {
    if (error && error?.graphQLErrors?.find((error) => error.message === 'Not found')) {
      notifications.error({
        title: 'A/B Test Not Found',
        content: `Couldn't find the A/B Test you were looking for`,
        timeout: 3000,
        skipHoneybadger: true,
      });
      navigate(
        {
          pathname: '/ab-tests',
          search: location.search,
        },
        { replace: true },
      );
    }
  }, [error, navigate, location.search, notifications]);

  const setTestDateRange = useCallback(
    (value) => {
      // we need to do a comparison to avoid the DateRangePicker
      // triggering an infinite rerender, since it calls the `setDateRange`
      // function from inside a useEffect
      if (
        (value.start_date && value.start_date !== selection.startDate) ||
        (value.end_date && value.end_date !== selection.endDate)
      ) {
        const newStartDate = getUnixTime(value.start_date);
        const newEndDate = getUnixTime(value.end_date);
        setDateSelection({
          special: null,
          startDate: newStartDate,
          endDate: newEndDate,
        });
        paramSet('date_range', `${newStartDate},${newEndDate}`);
      }
    },
    [selection.endDate, selection.startDate, setDateSelection, paramSet],
  );

  const data = resultData?.abTestDetailV2 ?? null;

  const setSpecialRange = useCallback(
    ({ special }) => {
      if (special && special !== selection.special) {
        const { startDate, endDate } =
          special === 'default' ? getSpecialDateRangeForTest(data) : getConvertedDateRange({ special }, true);
        setDateSelection({ special, startDate, endDate });
        if (special) {
          paramSet('date_range', special);
        } else {
          paramSet('date_range', `${startDate},${endDate}`);
        }
      }
    },
    [selection.special, setDateSelection, data, paramSet],
  );

  const testDefaultDateRange = useMemo(() => {
    if (!data) return { startDate: null, endDate: null };
    return getSpecialDateRangeForTest(data);
  }, [data]);

  const backTo = new URLSearchParams(location.search).get('back');
  const isBackToGoal = backTo && backTo.startsWith('/goals/');
  const canGoBackToDash = isBackToGoal
    ? permissions.can('navigate', FEATURES.GOALS).allowed
    : permissions.can('navigate', FEATURES.AB_TESTING).allowed;

  const addNewVariant = useCallback(
    (editorPatch) => {
      if (data.type === abTestTypes.SPLIT) {
        modal.show(<AddSplitVariant abTest={data} />);
      } else {
        navigate(
          {
            pathname: `/ab-tests/${id}/variants/new`,
          },
          {
            replace: true,
            state: { editorPatch },
          },
        );
      }
    },
    [data, navigate, id, modal],
  );

  const editVariant = useCallback(
    (abTest, variant) => {
      if (abTest.type === abTestTypes.SPLIT) {
        modal.show(<EditSplitVariant abTest={abTest} variant={variant} />);
      } else {
        navigate(`/ab-tests/${abTest.id}/variants/${variant.id}/edit`);
      }
    },
    [modal, navigate],
  );

  const setView = useCallback(
    (value) => {
      setDataset(value);
      paramSet('view', value);
    },
    [setDataset, paramSet],
  );

  const isDraft = data?.status === AB_TEST_STATUSES.DRAFT;
  const hasNotes = typeof data?.notes === 'string' && data?.notes?.trim?.() !== '';

  const variantsTableWidth = useMemo(() => variantsTableRef.current?.clientWidth ?? null, []);

  if (isDraft) {
    return <Navigate to={`/ab-tests/${id}/edit`} replace={true} />;
  }

  if (error && isSharedResourceSubscriptionError(error)) {
    return <Navigate replace to="/not-found" />;
  }

  const goalsList = goalsData?.goalRefList || data?.conversionGoals;

  return (
    <DashboardPage>
      <DashHeader
        titleComponent={
          <Breadcrumb>
            {!isSharing && (
              <BreadcrumbItem active={false} to="/ab-tests">
                A/B Testing
              </BreadcrumbItem>
            )}
            {isInitialLoading || !!data ? (
              <BreadcrumbItem active={true}>
                <div className="mr-5 flex items-center space-x-1">
                  {data?.name ? (
                    <span className="truncate">{data.name}</span>
                  ) : isInitialLoading ? (
                    <SkeletonLine width="300px" height="16px" />
                  ) : null}
                </div>
              </BreadcrumbItem>
            ) : null}
          </Breadcrumb>
        }
        actionButton={
          <div className="flex flex-shrink-0 items-center space-x-2">
            {canGoBackToDash ? (
              <LinkWithParams className="text-link flex items-center" to={backTo || '/ab-tests'} excluded={['back']}>
                <ArrowIcon className="mr-2.5 h-3 w-3 shrink-0 rotate-180 fill-current" /> Go back to{' '}
                {isBackToGoal ? 'goal' : 'tests'}
              </LinkWithParams>
            ) : null}
            <AbTestActionButton
              entity={data}
              redirectOnDelete="/ab-tests"
              refetch={refetch}
              addNewVariant={addNewVariant}
              editVariant={editVariant}
            />
            <CreateAbTestButton />
          </div>
        }
      />

      <div
        className="box-content p-10"
        style={{ minWidth: variantsTableWidth ? `${variantsTableWidth + 2}px` : undefined }}
      >
        <SEO title="A/B Testing Result" />
        {(isFetching && !data) || isInitialLoading ? (
          <div className="mt-16 flex w-full items-center justify-center">
            <Spinner />
            <div className="ml-2.5">Loading...</div>
          </div>
        ) : (!isFetching && !data) || error ? (
          <AbTestNotFoundWall />
        ) : (
          <>
            <div className="mb-[15px] flex items-end justify-between">
              <span className="text-body-5">
                Test published: {formatLongDate(data?.publishedAt)}
                {data?.publishedBy?.name ? ` by ${data.publishedBy.name}` : ''}
              </span>
              <div className="flex items-center space-x-5">
                <Button
                  variant="cancel"
                  onClick={() => modal.show(<AbTestDetailModal data={data} />)}
                  className="!px-0 !text-dodger-blue-500"
                >
                  <InfoIcon className="mr-1.5 h-4 w-4 fill-current" />
                  Test details
                </Button>

                <Button
                  variant="cancel"
                  onClick={() => modal.show(<NotesModal abTest={data} onSave={() => refetch?.()} />)}
                  className="!px-0 !text-dodger-blue-500"
                >
                  {hasNotes ? (
                    <NotesAddedIcon className="mr-1.5 h-4 w-4 fill-current" />
                  ) : (
                    <NotesIcon className="mr-1.5 h-4 w-4 fill-current" />
                  )}
                  Notes
                </Button>

                {selection.special === 'default' && !data ? null : (
                  <DateRangePicker
                    startDate={
                      selection.special === 'default'
                        ? fromUnixTime(testDefaultDateRange.startDate)
                        : fromUnixTime(selection.startDate)
                    }
                    endDate={
                      selection.special === 'default'
                        ? fromUnixTime(testDefaultDateRange.endDate)
                        : fromUnixTime(selection.endDate)
                    }
                    lowerBoundary={startOfDay(fromUnixTime(data.publishedAt))}
                    upperBoundary={data.stoppedAt ? endOfDay(fromUnixTime(data.stoppedAt)) : endOfDay(new Date())}
                    setCommonDateRange={setSpecialRange}
                    size="lg"
                    special={selection.special === 'default' ? null : selection.special}
                    tooltipOptions={{}}
                    setDateRange={setTestDateRange}
                    showCommonRanges={true}
                    relativeTo={data.stoppedAt}
                    maxRange={365}
                    timezone={TIMEZONES.UTC}
                  />
                )}
              </div>
            </div>

            <SummaryRow>
              <SummaryGoalSelector
                goals={goalsList}
                selected={selectedGoal}
                onClick={(goal) => {
                  if (goal.id) {
                    setGoal({
                      id: Number(goal.id),
                      name: goal.name,
                    });
                  }
                }}
                loading={(!isSharing && !goalsData) || isFetchingGoals}
              />
              <SummaryCard
                title="Unique Visitors"
                value={data?.totalVisitors?.toLocaleString?.() ?? 0}
                onClick={() => {
                  setView('visitors');
                }}
                selected={dataset === 'visitors'}
              />
              <SummaryCard
                title="Conversions"
                value={data?.totalConversions?.toLocaleString?.() ?? 0}
                onClick={() => {
                  setView('conversions');
                }}
                selected={dataset === 'conversions'}
              />
              <SummaryCard
                title="Conversion Rate"
                value={`${data?.avgConversionRate?.toLocaleString?.() ?? 0}%`}
                unit="avg"
                onClick={() => {
                  setView('conversion_rate');
                }}
                selected={dataset === 'conversion_rate'}
              />
              {/* <SummaryCard title="Avg session worth" value="$6" onClick={() => setDataset('avg_session_worth')} selected={dataset === 'avg_session_worth'} /> */}
              <SummaryCard
                title="Total Conversion Value"
                value={formatCurrency(data?.totalValue ?? 0, defaultCurrency, { precision: 10 ** 4 })}
                onClick={() => {
                  setView('value');
                }}
                selected={dataset === 'value'}
              />
            </SummaryRow>

            <DistributionChart
              abTest={data}
              goal={selectedGoal}
              counts={{ totalConversions: data?.totalConversions, totalVisitors: data?.totalVisitors }}
              isFetching={isFetching}
            />
            <VariantsTable
              abTest={data}
              variantsTableRef={variantsTableRef}
              refetch={refetch}
              editVariant={editVariant}
              addNewVariant={addNewVariant}
              enableRetiredVariantsAtom={enableRetiredVariantsAtom}
            />
          </>
        )}
      </div>
    </DashboardPage>
  );
}

export function AbTestsResult() {
  return (
    <SelectionProvider>
      <AbTestResults />
    </SelectionProvider>
  );
}

function VariantsTable({
  abTest,
  variantsTableRef,
  refetch = null,
  editVariant = null,
  addNewVariant = null,
  enableRetiredVariantsAtom,
}) {
  const modal = useModal();
  const { variants } = abTest;
  const { currentSelection, removeFromSelection, addToSelection, clearSelection, toggleSelection } = useSelection();
  const hasRetiredVariants = useMemo(() => variants.some((v) => v.isRetired), [variants]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuthContext();
  const defaultCurrency = currentUser?.settings?.defaultCurrency ?? 'USD';
  const [showRetiredVariants, setShowRetiredVariants] = useAtom(enableRetiredVariantsAtom);
  const permissions = usePermissions();
  const canEditGoals = permissions.can('edit', FEATURES.GOALS).allowed;

  const parsedVariants = useMemo(() => {
    if (!showRetiredVariants) {
      return variants.filter((v) => !v.isRetired);
    }
    return variants;
  }, [variants, showRetiredVariants]);

  const columns = useMemo(() => {
    return [
      {
        id: 'name',
        accessorKey: 'variantName',
        meta: {
          align: 'center',
          justify: 'left',
          padding: '0',
        },
        header: () => (
          <div className="flex items-center space-x-5">
            <div className="pl-3.75">
              <Checkbox
                checked={currentSelection.length && currentSelection.length === parsedVariants.length ? true : false}
                onChange={() => {
                  currentSelection.length === parsedVariants.total
                    ? clearSelection()
                    : parsedVariants.every((x) => currentSelection.find((y) => x.id === y.id))
                      ? removeFromSelection([...parsedVariants])
                      : addToSelection([...parsedVariants]);
                }}
                indeterminate={currentSelection.length > 0 && currentSelection.length < parsedVariants.total}
                title="Toggle all rows selected"
                size="lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <span className="font-bold">Variants</span>
            {hasRetiredVariants ? (
              /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
              <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  id="showRetiredVariants"
                  label={<span className="text-body-2 !normal-case">Show retired variants</span>}
                  value={showRetiredVariants}
                  checked={showRetiredVariants}
                  onChange={() =>
                    setShowRetiredVariants((currentValue) => {
                      // if currentSelection contains any retired variants and we toggle this off, remove them
                      const newValue = !currentValue;

                      if (!newValue) {
                        const retiredVariants = currentSelection.filter((v) => v.isRetired);
                        removeFromSelection([...retiredVariants]);
                      }

                      return newValue;
                    })
                  }
                  size="lg"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            ) : null}
          </div>
        ),
        cell({ row }) {
          const selectedIndex = currentSelection.findIndex((x) => x.id === row.original.id);
          const selected = currentSelection.filter((x) => x.id === row.original.id);
          return (
            <div className="flex h-full w-full items-center">
              {selected.length > 0 && <VariantColor position={row.original.position} type={row.original.type} />}
              <div className="ml-5 mr-[14px]">
                <Checkbox
                  checked={selectedIndex > -1}
                  onChange={() => toggleSelection({ ...row.original })}
                  size="lg"
                />
              </div>

              <div
                className={classNames(
                  'group relative mr-6 h-10 w-[65px] overflow-hidden rounded border border-[#E1E6EF] bg-mystic-500 flex-shrink-0',
                  {
                    'cursor-pointer': !!row.original?.screenshotUrl,
                  },
                )}
              >
                <LazyImg
                  className="mr-5 !h-10 w-[65px] rounded !border !border-mystic-500 object-cover"
                  alt="variant thumbnail"
                  Placeholder={ImgPlaceholderIcon}
                  src={row.original?.screenshotUrl ?? null}
                  width="65px"
                  height="40px"
                  onClick={() => {
                    if (!row.original?.screenshotUrl) return;
                    modal.show(<VariantScreenshotModal variant={row.original} />);
                  }}
                />
                {row.original?.screenshotUrl ? (
                  <div className="pointer-events-none absolute bottom-0 right-0 hidden h-10 w-16 items-center justify-center rounded bg-regal-blue-700 bg-opacity-60 border border-[#E1E6EF] group-hover:flex">
                    <EyeIcon className="h-4 w-4 fill-current text-white" />
                  </div>
                ) : null}
              </div>
              <div className="flex flex-col truncate">
                <div className="flex items-center space-x-2.5">
                  <span
                    className={classNames({
                      'text-body-1': !row.original.isRetired,
                      'text-body-3 font-semibold': row.original.isRetired,
                    })}
                  >
                    {row.original.variantName}
                    {row.original.isRetired ? ` (retired)` : ''}
                  </span>
                  {typeof row.original.description === 'string' && row.original.description.trim() !== '' ? (
                    <Tooltip tooltipContent={<div className="max-w-[174px] text-left">{row.original.description}</div>}>
                      <DescriptionIcon className="h-[14px] w-[14px] fill-current text-cadet-blue-500" />
                    </Tooltip>
                  ) : null}
                </div>
                <a
                  className="text-body-2 text-link truncate"
                  href={row.original.viewVariantUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={displayUrl(row.original.redirectUrl || abTest.pageUrl)}
                >
                  {displayUrl(row.original.redirectUrl || abTest.pageUrl)}
                </a>
              </div>
            </div>
          );
        },
        size: 300,
      },
      {
        id: 'visitors',
        accessorKey: 'results.visitors',
        meta: {
          align: 'center',
          justify: 'right',
        },
        header: () => (
          <div>
            <div className="text-right font-bold w-full">
              Unique
              <br />
              Visitors
            </div>
          </div>
        ),
        cell({ row }) {
          return <span className="text-right w-full">{(row.original.results.visitors || 0).toLocaleString()}</span>;
        },
      },
      {
        id: 'conversions',
        header: () => (
          <div>
            <div className="text-right font-bold">Conversions</div>
          </div>
        ),
        accessorKey: 'results.conversions',
        meta: {
          align: 'center',
          justify: 'right',
        },
        cell({ row }) {
          return <span className="text-right w-full">{(row.original.results.conversions || 0).toLocaleString()}</span>;
        },
      },
      {
        id: 'conversionRate',
        accessorKey: 'results.conversion_rate',
        meta: {
          align: 'center',
          justify: 'right',
        },
        header: () => (
          <div>
            <div className="text-right font-bold">
              Conversion
              <br />
              Rate
            </div>
          </div>
        ),
        cell({ row }) {
          return (
            <div className="flex flex-col items-center text-right">
              <span>{(row.original.results.conversionRate || 0).toFixed(2).toLocaleString()}%</span>
              <RateDelta value={row.original.results.improvementFromControl} />
            </div>
          );
        },
      },
      {
        id: 'significance',
        accessorKey: 'significance',
        meta: {
          align: 'center',
          justify: 'right',
        },
        header: () => (
          <div className="pr-5">
            <Tooltip
              tooltipContent={
                <div className="max-w-[250px] text-left">
                  Statistical significance shows how likely your A/B test results are real, not random. 95% confidence
                  or higher is considered significant.
                </div>
              }
            >
              <div className="border-b border-dashed border-cadet-blue-500">
                <div className="text-right font-bold">
                  Statistical
                  <br />
                  Significance
                </div>
              </div>
            </Tooltip>
          </div>
        ),
        cell({ row }) {
          if (row.original.significance && row.original.significance < 1) {
            return <span className="pr-5">&lt;1%</span>;
          }
          if (row.original.significance) {
            const finalValue = Math.round(row.original.significance);
            const isSignificant = finalValue >= 95;
            return (
              <span
                className={classNames('pr-5', {
                  'font-semibold text-lima-500': isSignificant,
                })}
              >
                {finalValue}%
              </span>
            );
          }
          return <span className="pr-5">0%</span>;
        },
      },
      // {
      //   id: 'avg_value',
      //   header: () => <span className="text-center">Avg Session<br />Worth (USD)</span>,
      //   meta: {
      //     align: 'center',
      //     justify: 'center',
      //     headerCellClassnames: 'border-l border-mystic-500',
      //     rowCellClassnames: 'border-l border-mystic-500',
      //   },
      //   cell() {
      //     return <span>a</span>
      //   }
      // },
      {
        id: 'value',
        accessorKey: 'results.total_value',
        meta: {
          align: 'center',
          justify: 'center',
          headerCellClassnames: 'border-l border-mystic-500',
          rowCellClassnames: 'border-l border-mystic-500',
        },
        header: () => (
          <div>
            <div className="text-right font-bold">
              Total Conversion
              <br />
              Value ({defaultCurrency})
            </div>
          </div>
        ),
        cell({ row }) {
          if (abTest.selectedGoal.hasValue || row.original.results.value > 0) {
            return <span>{formatCurrency(row.original.results.value, defaultCurrency, { precision: 10 ** 4 })}</span>;
          }
          if (!canEditGoals) {
            return <span className="text-body-3">Not set</span>;
          }
          return (
            <AppLink to={`/goals/${abTest.selectedGoal.id}/edit-value`} className="text-link">
              Set value
            </AppLink>
          );
        },
      },
      {
        id: 'actions',
        enableSorting: false,
        minSize: 60,
        maxSize: 60,
        meta: {
          align: 'center',
          justify: 'center',
        },
        cell({ row }) {
          return (
            <VariantActionButton
              abTest={abTest}
              entity={row.original}
              refetch={refetch}
              editVariant={editVariant}
              addNewVariant={addNewVariant}
              removeSpacing={true}
            />
          );
        },
      },
    ];
  }, [
    refetch,
    abTest,
    currentSelection,
    removeFromSelection,
    addToSelection,
    toggleSelection,
    clearSelection,
    parsedVariants,
    showRetiredVariants,
    hasRetiredVariants,
    addNewVariant,
    defaultCurrency,
    editVariant,
    modal,
    setShowRetiredVariants,
    canEditGoals,
  ]);

  const tableSortData = useMemo(() => {
    return {
      id: snakeToCamel(searchParams.get('order') || 'NAME'),
      desc: (searchParams.get('sort') || SORT_ORDER_TYPES.ASC) === SORT_ORDER_TYPES.DESC,
    };
  }, [searchParams]);

  const onFetchData = useCallback(
    ({ sorting }) => {
      if (sorting) {
        setSearchParams({
          order: camelToSnake(sorting[0].id).toUpperCase(),
          sort: sorting[0].desc ? SORT_ORDER_TYPES.DESC : SORT_ORDER_TYPES.ASC,
        });
      }
    },
    [setSearchParams],
  );

  return (
    <div
      className="sharp-table-edges overflow-y-auto border border-mystic-500 bg-white rounded-bl rounded-br w-full"
      style={{
        boxShadow: '0 4px 6px 0 rgba(7,31,49,0.07)',
      }}
    >
      <ReactTable
        fetching={false}
        columns={columns}
        data={parsedVariants}
        ref={{ tableRef: variantsTableRef }}
        className="!border-none !rounded-none !shadow-md"
        rowCustomBackground={(row) =>
          classNames({
            'bg-white': row,
          })
        }
        rowCustomClassname={() =>
          classNames({
            'after:!border-solid': true,
          })
        }
        enableSorting={true}
        sorting={tableSortData}
        onFetchData={onFetchData}
      />
    </div>
  );
}

function VariantColor({ position, type }) {
  if (!position || position === -1) {
    return <div className="absolute bottom-px top-0 w-[5px] pb-px">&nbsp;</div>;
  }

  const colorMatrix = [
    'bg-dodger-blue-300',
    'bg-lima-500',
    'bg-pink-salmon-500',
    'bg-dandelion-500',
    'bg-dodger-blue-500',
    'bg-wisteria-500',
    'bg-[#8EDEC5]',
    'bg-[#FFAF8F]',
    'bg-[#FF7679]',
  ];

  const color = colorMatrix[position] ?? 'bg-[#A5ACBC]';

  return (
    <div
      className={classNames('absolute bottom-px top-0 w-[5px] pb-px', {
        [color]: type !== abTestVariantTypes.CONTROL,
        'bg-lavender-500': type === abTestVariantTypes.CONTROL,
      })}
    >
      &nbsp;
    </div>
  );
}
